import React, { useState } from "react";
import { Plus, Edit2, Trash2, FileText } from "lucide-react";
import { ConfigSection } from "../ui/ConfigSection";
import { Button } from "../../../components/ui";
import { QuotationTermModal } from "../modals/QuotationTermModal";
import { InvoiceTermModal } from "../modals/InvoiceTermModal";
import { useQuotationTerms } from "../hooks/useQuotationTerms";
import { useInvoiceTerms } from "../hooks/useInvoiceTerms";

export default function TermsAndConditionsTab() {
    const [activeSubTab, setActiveSubTab] = useState("quotation");

    const quotation = useQuotationTerms();
    const invoice = useInvoiceTerms();

    const active = activeSubTab === "quotation" ? quotation : invoice;
    const isQuotation = activeSubTab === "quotation";

    const [modalOpen, setModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);

    return (
        <div className="max-w-5xl space-y-8 animate-in fade-in duration-500 pb-20">

            {/* Sub-tabs for Quotation vs Invoice */}
            <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-xl w-max mb-6">
                <button
                    onClick={() => setActiveSubTab("quotation")}
                    className={`px-6 py-2.5 rounded-lg text-[13px] font-bold transition-all ${activeSubTab === "quotation"
                        ? 'bg-white text-orange-600 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'}`}
                >
                    Quotation Terms
                </button>
                <button
                    onClick={() => setActiveSubTab("invoice")}
                    className={`px-6 py-2.5 rounded-lg text-[13px] font-bold transition-all ${activeSubTab === "invoice"
                        ? 'bg-white text-orange-600 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'}`}
                >
                    Invoice Terms
                </button>
            </div>

            <ConfigSection
                title={isQuotation ? "Master Quotation Terms" : "Master Invoice Terms"}
                description={isQuotation
                    ? "Manage and enable legal clauses for your Verve quotations."
                    : "Manage and enable legal clauses for your project invoices."}
                dotColor={isQuotation ? "bg-purple-500" : "bg-blue-500"}
            >
                <div className="flex justify-end mb-4 -mt-12">
                    <Button
                        variant="orange"
                        leftIcon={<Plus size={14} />}
                        onClick={() => { setEditingItem(null); setModalOpen(true); }}
                        className="shadow-md shadow-orange-100/50 font-bold rounded-lg h-[32px] text-[11px]"
                    >
                        Add New
                    </Button>
                </div>

                {active.loading ? (
                    <div className="py-20 text-center text-slate-400">Loading terms...</div>
                ) : active.terms.length > 0 ? (
                    <div className="space-y-3">
                        {active.terms.map((item) => (
                            <div
                                key={item._id}
                                className="flex items-center justify-between border border-slate-200/60 rounded-2xl p-5 bg-white hover:border-brand-blue/30 transition-all shadow-sm group"
                            >
                                <div className="min-w-0 pr-6 flex items-center gap-4">
                                    <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center text-slate-400 shrink-0 group-hover:bg-brand-blue/10 group-hover:text-brand-blue transition-colors">
                                        <FileText size={20} />
                                    </div>
                                    <div>
                                        <p className="text-[14px] font-bold text-slate-800 leading-tight">
                                            {item.content.split('\n')[0].substring(0, 100)}{item.content.length > 100 ? "..." : ""}
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-6 shrink-0">
                                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button
                                            onClick={() => { setEditingItem(item); setModalOpen(true); }}
                                            className="p-1.5 text-slate-400 hover:text-brand-blue hover:bg-brand-blue/5 rounded-md transition-all"
                                        >
                                            <Edit2 size={15} />
                                        </button>
                                        <button
                                            onClick={() => active.deleteTerm(item._id)}
                                            className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-all"
                                        >
                                            <Trash2 size={15} />
                                        </button>
                                    </div>

                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            className="sr-only peer"
                                            checked={item.isActive}
                                            onChange={() => active.toggleActive(item._id, item.isActive)}
                                        />
                                        <div className="w-11 h-6 bg-slate-200 peer-focus:ring-2 peer-focus:ring-brand-blue rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-brand-blue after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all shadow-inner" />
                                    </label>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="py-24 text-center bg-slate-50/30 rounded-3xl border-2 border-dashed border-slate-200">
                        <div className="w-16 h-16 bg-white shadow-sm border border-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4 text-slate-300">
                            <FileText size={32} />
                        </div>
                        <h4 className="text-lg font-bold text-slate-700 uppercase tracking-tight">No {isQuotation ? "Quotation" : "Invoice"} Terms Found</h4>
                        <p className="text-slate-400 mb-8 text-sm max-w-xs mx-auto">Start by adding a standard legal clause for your {isQuotation ? "quotation" : "invoice"} documents.</p>
                        <Button
                            variant="orange"
                            leftIcon={<Plus size={14} />}
                            onClick={() => { setEditingItem(null); setModalOpen(true); }}
                            className="rounded-lg font-bold px-6 shadow-md shadow-orange-100/50"
                        >
                            Add First Clause
                        </Button>
                    </div>
                )}
            </ConfigSection>

            {modalOpen && isQuotation && (
                <QuotationTermModal
                    isOpen={modalOpen}
                    onClose={() => setModalOpen(false)}
                    onSuccess={quotation.fetchData}
                    initialData={editingItem}
                />
            )}

            {modalOpen && !isQuotation && (
                <InvoiceTermModal
                    isOpen={modalOpen}
                    onClose={() => setModalOpen(false)}
                    onSuccess={invoice.fetchData}
                    initialData={editingItem}
                />
            )}
        </div>
    );
}
